// =============================================================================
// SIREPPAR - Hook: estado del apoderado de un reconocimiento en formación
// =============================================================================
// Centraliza el gating de la navegación y del dashboard para el apoderado de un
// partido en formación con expediente de RECONOCIMIENTO. Reusa el queryKey
// ['mi-expediente'] (react-query deduplica con el DashboardPage).
//
// El backend expone en GET /expedientes/mi-expediente la `fase` y la bandera
// `documentacionCargada` (ver validators/datosFormularioReconocimiento).
// =============================================================================

import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { esEtapaAfiliados, esEtapaAutoridades } from '../utils/fasesReconocimiento';

const ROLES_INTERNOS = ['ADMINISTRADOR', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE', 'MESA_ENTRADAS', 'ANALISTA', 'AUDITOR'];

// Fases anteriores al reconocimiento provisorio: el partido sigue "en formación"
// y la navegación se restringe. DOCUMENTACION_APROBADA (provisorio) y posteriores
// devuelven la navegación externa completa (carga de autoridades reales).
const FASES_EN_FORMACION = [
  'WIZARD_EXTERNO_COMPLETO',
  'ATRIBUTOS_PENDIENTES', 'ATRIBUTOS_A_PUBLICAR', 'ATRIBUTOS_PUBLICADOS', 'ATRIBUTOS_EN_TEP',
  'ATRIBUTOS_RECHAZADOS', 'ATRIBUTOS_APROBADOS',
  'DOCUMENTACION_PENDIENTE', 'DOCUMENTACION_EN_SECRETARIO',
  'DOCUMENTACION_EN_TEP', 'DOCUMENTACION_RECHAZADA',
];

export interface MiExpediente {
  id: string;
  numero: string;
  estado: string;
  tipo: string;
  fase?: string | null;
  documentacionCargada?: boolean;
  autoridadesCargadas?: boolean;
  partidoEstado?: string | null;
  // Vencimiento del plazo de 4 meses del provisorio. Es UNO SOLO para las dos
  // etapas (afiliados y autoridades definitivas): corre desde la resolución del
  // TEP que otorgó el provisorio. El nombre quedó de la etapa de afiliados.
  plazoAfiliadosVence?: string | null;
}

export function useReconocimientoApoderado() {
  const { user } = useAuth();
  const esExterno = !!user?.partidoId && !ROLES_INTERNOS.includes(user?.rol || '');

  const { data: miExpediente, isLoading } = useQuery<MiExpediente | null>({
    queryKey: ['mi-expediente'],
    queryFn: async () => (await api.get('/expedientes/mi-expediente')).data.data,
    enabled: esExterno,
  });

  const esReconocimiento = esExterno && miExpediente?.tipo === 'RECONOCIMIENTO';
  const fase = miExpediente?.fase ?? null;
  const esReconocimientoEnFormacion = !!(esReconocimiento && fase && FASES_EN_FORMACION.includes(fase));
  const documentacionCargada = !!miExpediente?.documentacionCargada;
  // Etapa de afiliados del provisorio: el partido tiene 4 meses para presentar el
  // padrón (4‰). La navegación se acota a Inicio + Afiliados + Usuarios para que
  // no se pierda entre las demás secciones, igual que durante el wizard.
  const enEtapaAfiliados = !!(esReconocimiento && esEtapaAfiliados(fase));
  // Etapa de autoridades definitivas: el partido tiene que hacer sus elecciones
  // internas y cargar las autoridades electas + apoderados, dentro del MISMO plazo
  // de 4 meses del provisorio. La navegación se acota igual que en las otras
  // etapas, dejando /autoridades para que pueda ver la nómina.
  const enEtapaAutoridades = !!(esReconocimiento && esEtapaAutoridades(fase));
  const autoridadesCargadas = !!miExpediente?.autoridadesCargadas;
  const mostrarWizardAutoridades = enEtapaAutoridades && !autoridadesCargadas;
  // El wizard se muestra en DOCUMENTACION_PENDIENTE hasta que el apoderado carga.
  const mostrarWizard = esReconocimientoEnFormacion && fase === 'DOCUMENTACION_PENDIENTE' && !documentacionCargada;

  // Después de que el partido presentó sus autoridades, el trámite sigue del lado
  // interno: primero el Secretario Electoral y después el Tribunal. El partido no
  // tiene nada que hacer, pero SÍ tiene que ver dónde está su trámite — antes acá
  // se quedaba sin ningún cartel en el inicio, como si el trámite hubiera
  // terminado. Si el Secretario devuelve el expediente, la fase vuelve a
  // AUTORIDADES_PENDIENTES y reaparece el cartel de la etapa.
  const enSecretarioDefinitivo = !!(esReconocimiento && fase === 'DEFINITIVO_EN_SECRETARIO');
  const enTepDefinitivo = !!(esReconocimiento && fase === 'DEFINITIVO_EN_TEP');
  const enTramiteDefinitivo = enSecretarioDefinitivo || enTepDefinitivo;

  // El TEP otorgó el reconocimiento DEFINITIVO: fin del trámite. Habilita el cartel
  // de felicitaciones del inicio (que el partido puede cerrar).
  const obtuvoDefinitivo = !!(esReconocimiento && fase === 'RECONOCIMIENTO_DEFINITIVO');

  // Mi Partido y Financiamiento sólo se habilitan cuando el reconocimiento quedó
  // definitivo: partido VIGENTE (o expediente VIGENTE). Antes de eso el partido
  // todavía está en trámite y esas secciones no aplican.
  const esVigente = miExpediente?.partidoEstado === 'VIGENTE' || miExpediente?.estado === 'VIGENTE';
  // Sólo restringimos al externo con expediente de RECONOCIMIENTO; el resto
  // (reempadronamiento, partidos ya existentes) conserva la navegación completa.
  const restringirSeccionesVigente = !!(esReconocimiento && !esVigente);

  // FINANCIAMIENTO tiene una condición propia, más amplia que la de Mi Partido:
  // también se oculta en REEMPADRONAMIENTO mientras el partido no esté VIGENTE.
  // Un partido en trámite todavía no está reconocido, así que no tiene aporte
  // público que rendir ni rendición que presentar; la sección le mostraba un módulo
  // completo que no le corresponde. Mi Partido, en cambio, se le sigue mostrando en
  // reempadronamiento: el partido ya existe y sus datos son los que está
  // reempadronando.
  const esReempadronamiento = esExterno && miExpediente?.tipo === 'REEMPADRONAMIENTO';
  const restringirFinanciamiento = !!((esReconocimiento || esReempadronamiento) && !esVigente);

  // Fin del trámite de reempadronamiento: lo resolvió a favor el Secretario
  // Electoral (o el TEP) y la agrupación quedó VIGENTE. Habilita el cartel de
  // felicitaciones del inicio, igual que `obtuvoDefinitivo` en reconocimiento.
  //
  // Se mira `esVigente` y NO un estado puntual del expediente porque los dos
  // caminos de aprobación —la resolución del Secretario y el acta del TEP— dejan
  // exactamente el mismo resultado, y el partido no tiene por qué ver la
  // diferencia. El desbloqueo del menú (`restringirFinanciamiento`) ya salía de la
  // misma bandera, así que las dos cosas se encienden juntas.
  const obtuvoReempadronamiento = !!(esReempadronamiento && esVigente);

  return {
    isLoading,
    miExpediente: miExpediente ?? null,
    expedienteId: miExpediente?.id ?? null,
    esReconocimientoEnFormacion,
    fase,
    documentacionCargada,
    enEtapaAfiliados,
    enEtapaAutoridades,
    autoridadesCargadas,
    mostrarWizardAutoridades,
    enSecretarioDefinitivo,
    enTepDefinitivo,
    enTramiteDefinitivo,
    obtuvoDefinitivo,
    obtuvoReempadronamiento,
    mostrarWizard,
    esVigente,
    restringirSeccionesVigente,
    restringirFinanciamiento,
  };
}
